import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import { IQueryGetEntitiesFieldArgs } from './types';
import { useMemo } from 'react';
import {
  getFirstPaginatedNode,
  getPaginationTotalResults,
  toPagination,
} from './utils';

export const GET_ENTITIES_IP_ADDRESS_OPEN_PORTS = graphql(`
  query getIpAddressOpenPorts($pagination: PaginationRequest, $uuid: String) {
    entities(filters: { submissionUuid: $uuid, submissionType: INSURED }) {
      edges {
        cursor
        node {
          ipAddresses(pagination: $pagination) {
            pageInfo {
              totalResults
              pageNumber
              pageSize
              hasNextPage
              hasPreviousPage
            }
            edges {
              cursor
              node {
                ipv4Address
                ipv6Address
                software {
                  edges {
                    node {
                      ports {
                        edges {
                          node {
                            port
                            protocol
                          }
                        }
                        pageInfo {
                          totalResults
                        }
                      }
                    }
                  }
                  pageInfo {
                    totalResults
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

export function useGetEntitiesIpAddressOpenPortsQuery({
  submissionUuid,
  pageNumber,
  pageSize,
}: IQueryGetEntitiesFieldArgs) {
  const { loading, error, data } = useQuery(
    GET_ENTITIES_IP_ADDRESS_OPEN_PORTS,
    {
      variables: {
        uuid: submissionUuid,
        pagination: toPagination(pageNumber, pageSize),
      },
    }
  );

  const { ipAddresses, totalIpAddresses } = useMemo(() => {
    if (loading) {
      return {
        ipAddressesData: [],
        totalIpAddresses: 0,
      };
    }
    const entity = getFirstPaginatedNode(data?.entities);
    const ipAddresses = entity?.ipAddresses.edges?.length
      ? entity?.ipAddresses.edges.flatMap(({ node: result }) => {
          const ports = result?.software?.edges.flatMap(({ node: software }) =>
            software?.ports?.edges ? software?.ports?.edges : []
          );
          if (!ports.length) {
            return [
              {
                ip: result?.ipv4Address,
                isFirstRecord: true,
                totalRecords: 1,
              },
            ];
          } else {
            return ports.map((openPort, index) => ({
              ip: result?.ipv4Address,
              port: openPort.node.port,
              protocol: openPort.node.protocol,
              isFirstRecord: index === 0,
              totalRecords: ports.length,
            }));
          }
        })
      : [];
    const totalIpAddresses = getPaginationTotalResults(entity?.ipAddresses);
    return {
      ipAddresses,
      totalIpAddresses,
    };
  }, [data, loading]);

  return {
    loading,
    error,
    data,
    ipAddresses,
    totalIpAddresses,
  };
}
